import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaTrash } from "react-icons/fa";
import {
  Container,
  Row,
  Col,
  Button,
  Form,
  Table,
  Spinner,
} from "react-bootstrap";
import Swal from "sweetalert2";

const NewBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    title: "",
    imageUrl: null,
    description: "",
  });

  const fetchBlogs = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/get-blogs`);
      if (res.data && Array.isArray(res.data.blogs)) {
        setBlogs(res.data.blogs);
      } else {
        setBlogs([]);
      }
    } catch (error) {
      Swal.fire("Error", "Failed to fetch blogs", "error");
      console.error("Error fetching blogs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      setForm({ ...form, [name]: files[0] });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { title, imageUrl, description } = form;

    if (!title || !imageUrl || !description) {
      Swal.fire("Error", "All fields are required!", "error");
      return;
    }

    const now = new Date();
    const date = now.toISOString().split("T")[0]; // YYYY-MM-DD
    const time = now.toTimeString().split(" ")[0]; // HH:MM:SS

    const formData = new FormData();
    formData.append("title", title);
    formData.append("imageUrl", imageUrl);
    formData.append("description", description);
    formData.append("date", date);
    formData.append("time", time);

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/add-blog`, formData);
      setForm({ title: "", imageUrl: null, description: "" });
      Swal.fire("Success", "Blog added successfully!", "success");
      fetchBlogs();
    } catch (err) {
      Swal.fire("Error", "Failed to add blog", "error");
      console.error("Error uploading blog:", err);
    }
  };

  const deleteBlog = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This will delete the blog permanently!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/delete-blog/${id}`
        );
        setBlogs(blogs.filter((blog) => blog.id !== id));
        Swal.fire("Deleted!", "Blog deleted successfully!", "success");
      } catch (error) {
        Swal.fire("Error", "Failed to delete blog", "error");
        console.error("Error deleting blog:", error);
      }
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  return (
    <Container className="my-5">
      <Row>
        <Col md={4}>
          <h2 className="text-primary mb-4">Add New Blog</h2>
          <Form
            onSubmit={handleSubmit}
            className=" p-4 rounded shadow-sm"
            style={{ backgroundColor: "aliceblue" }}
          >
            <Form.Group controlId="formTitle">
              <Form.Label className="fw-bold mb-2">Title</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={form.title}
                onChange={handleChange}
                placeholder="Enter blog title"
                required
              />
            </Form.Group>

            <Form.Group controlId="formImage" className="mt-3">
              <Form.Label className="fw-bold mb-2">Blog Image</Form.Label>
              <Form.Control
                type="file"
                name="imageUrl"
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group controlId="formDescription" className="mt-3">
              <Form.Label className="fw-bold mb-2">Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={form.description}
                onChange={handleChange}
                placeholder="Enter blog description"
                required
              />
            </Form.Group>

            <Button
              type="submit"
              variant="success"
              className="w-100 mt-4 fw-bold"
            >
              Add Blog
            </Button>
          </Form>
        </Col>

        <Col md={8}>
          <h2 className="text-primary mb-4">Blogs</h2>
          {loading ? (
            <div className="text-center mt-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : blogs.length === 0 ? (
            <p>No blogs found.</p>
          ) : (
            <Table
              style={{ backgroundColor: "aliceblue" }}
              bordered
              hover
              responsive
            >
              <thead className="table-primary">
                <tr>
                  <th>ID</th>
                  <th>Title</th>
                  <th>Image</th>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Description</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {blogs.map((blog, index) => (
                  <tr
                    key={blog.id || index}
                    style={{ justifyContent: "center" }}
                  >
                    <td>{blog.id}</td>
                    <td>{blog.title}</td>
                    <td>
                      {blog.imageUrl && (
                        <img
                          src={`${process.env.REACT_APP_API_URL}/${blog.imageUrl}`}
                          alt={blog.title}
                          width="100"
                          height="100"
                          style={{ objectFit: "cover" }}
                        />
                      )}
                    </td>
                    <td>{blog.date}</td>
                    <td>{blog.time}</td>
                    <td>{blog.description}</td>
                    <td>
                      <Button
                        variant="danger"
                        onClick={() => deleteBlog(blog.id)}
                      >
                        <FaTrash />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default NewBlogs;
